import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * SlideToOpen
 * Draggable "slide to unlock"-style button.
 * When the user drags the handle past the threshold, we navigate to `to`.
 * If `external` is truthy, the URL is opened as an external link instead.
 */
const SlideToOpen = ({
  to = "/",
  label = "slide to open",
  external = false,
  ariaLabel,
}) => {
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const handleRef = useRef(null);
  const [pos, setPos] = useState(0); // px offset of the handle from left
  const [dragging, setDragging] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const startXRef = useRef(0);
  const startPosRef = useRef(0);
  const maxRef = useRef(0);

  const computeMax = useCallback(() => {
    if (!trackRef.current || !handleRef.current) return 0;
    return trackRef.current.clientWidth - handleRef.current.clientWidth;
  }, []);

  const finish = useCallback(() => {
    setUnlocked(true);
    setTimeout(() => {
      if (external) {
        window.open(to, "_blank", "noopener,noreferrer");
        // reset for re-use
        setUnlocked(false);
        setPos(0);
      } else {
        navigate(to);
      }
    }, 260);
  }, [external, navigate, to]);

  const onPointerDown = (e) => {
    if (unlocked) return;
    e.preventDefault();
    setDragging(true);
    startXRef.current = e.clientX ?? (e.touches && e.touches[0].clientX) ?? 0;
    startPosRef.current = pos;
    maxRef.current = computeMax();
    if (handleRef.current && e.pointerId != null) {
      try {
        handleRef.current.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }
  };

  const onPointerMove = useCallback(
    (e) => {
      if (!dragging) return;
      const clientX =
        e.clientX ?? (e.touches && e.touches[0]?.clientX) ?? startXRef.current;
      const delta = clientX - startXRef.current;
      const next = Math.min(
        Math.max(0, startPosRef.current + delta),
        maxRef.current
      );
      setPos(next);
      if (next >= maxRef.current - 1) {
        setDragging(false);
        finish();
      }
    },
    [dragging, finish]
  );

  const onPointerUp = useCallback(() => {
    if (!dragging) return;
    setDragging(false);
    if (pos >= maxRef.current * 0.85) {
      setPos(maxRef.current);
      finish();
    } else {
      setPos(0);
    }
  }, [dragging, pos, finish]);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
  }, [dragging, onPointerMove, onPointerUp]);

  const progress = maxRef.current ? pos / maxRef.current : 0;

  return (
    <div
      className={`slide-to-open ${dragging ? "is-dragging" : ""} ${
        unlocked ? "is-unlocked" : ""
      }`}
      ref={trackRef}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel || label}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          maxRef.current = computeMax();
          setPos(maxRef.current);
          finish();
        }
      }}
    >
      <span
        className="slide-fill"
        style={{ width: `${(handleRef.current?.clientWidth || 0) + pos}px` }}
      />
      <span
        className="slide-label"
        style={{ opacity: Math.max(0, 1 - progress * 1.6) }}
      >
        {label}
      </span>
      <div
        ref={handleRef}
        className="slide-handle"
        style={{ transform: `translateX(${pos}px)` }}
        onPointerDown={onPointerDown}
      >
        <ArrowRight size={26} strokeWidth={2.5} className="slide-arrow" />
      </div>
    </div>
  );
};

export default SlideToOpen;
